import { tenantHeaders } from '../services/TenantService';
import { HIVE_URL } from '../runtimeConfig';
export type AccountDto = {
  id: number;
  name: string | null;
};

export type MeDto = {
  userId: number | null;
  userRoleId: number | null;
  /** FullName, else "First Last", else the email. Never a placeholder. */
  displayName: string | null;
  email: string | null;
  /** `_UserRole.RoleName`, e.g. "Admin", "Boogie Sales Rep". */
  roleName: string | null;
  homeAccountId: number | null;
  homeAccountName: string | null;
  actingAccountId: number | null;
  /** Null unless an admin is acting on a tenant other than their own. */
  actingAccountName: string | null;
  /** Authoritative: Hive recomputes this from the database on every call. */
  canSwitchTenant: boolean;
};

export type OrganizationRoleDto = {
  id: number;
  name: string;
  description: string | null;
  organizationId: number;
};

export type OrganizationUserDto = {
  id: number;
  email: string;
  fullname: string | null;
  organizationId: number;
  roles: OrganizationRoleDto[];
};

class AdminApi {
  private baseUrl: string;

  constructor() {
    this.baseUrl =
      HIVE_URL + "/api/admin";
  }

  private getToken(): string {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        return user.token || "";
      } catch {
        return "";
      }
    }
    return "";
  }

  private async request<T>(
    method: string,
    path: string,
    data?: unknown
  ): Promise<T> {
    const token = this.getToken();
    const options: RequestInit = {
      method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        ...tenantHeaders(),
      },
    };
    if (data && ["POST", "PUT", "PATCH"].includes(method)) {
      options.body = JSON.stringify(data);
    }
    const response = await fetch(`${this.baseUrl}${path}`, options);
    const json = await response.json();
    if (!response.ok || (json.status != null && json.status >= 400)) {
      throw new Error(json.error || json.message || "Request failed");
    }
    return json.data as T;
  }

  /**
   * Operating bakeries the caller may act on. A tenant admin gets every
   * bakery; everyone else gets only their own account.
   */
  getAccounts(): Promise<AccountDto[]> {
    return this.request<AccountDto[]>("GET", "/accounts");
  }

  /**
   * The server's own answer to "who am I and may I switch tenants".
   *
   * Preferred over inferring privilege from the stored login payload: that is
   * client-side and goes stale when a role changes, whereas this is resolved
   * from the database per request.
   */
  getMe(): Promise<MeDto> {
    return this.request<MeDto>("GET", "/me");
  }

  getRoles(): Promise<OrganizationRoleDto[]> {
    return this.request<OrganizationRoleDto[]>("GET", "/roles");
  }

  createRole(body: {
    name: string;
    description?: string | null;
  }): Promise<OrganizationRoleDto> {
    return this.request<OrganizationRoleDto>("POST", "/roles", body);
  }

  updateRole(
    id: number,
    body: { name?: string; description?: string | null }
  ): Promise<OrganizationRoleDto> {
    return this.request<OrganizationRoleDto>("PATCH", `/roles/${id}`, body);
  }

  deleteRole(id: number): Promise<{ id: number }> {
    return this.request<{ id: number }>("DELETE", `/roles/${id}`);
  }

  getUsers(): Promise<OrganizationUserDto[]> {
    return this.request<OrganizationUserDto[]>("GET", "/users");
  }

  createUser(body: {
    email: string;
    password: string;
    fullname?: string | null;
  }): Promise<OrganizationUserDto> {
    return this.request<OrganizationUserDto>("POST", "/users", body);
  }

  setUserRoles(
    userId: number,
    roleIds: number[]
  ): Promise<OrganizationUserDto> {
    return this.request<OrganizationUserDto>(
      "PATCH",
      `/users/${userId}/roles`,
      { roleIds }
    );
  }
}

export default new AdminApi();
